import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import { Skeleton } from "@veil/ui/components/skeleton";
import CircleAlert from "lucide-react/dist/esm/icons/circle-alert";
import MonitorPlay from "lucide-react/dist/esm/icons/monitor-play";
import SquareTerminal from "lucide-react/dist/esm/icons/square-terminal";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "wouter";
import {
	StopSessionDialog,
	type StopSessionTarget,
} from "../components/workers/stop-session-dialog";
import { api, unwrap } from "../lib/api";
import { sessionThumbnailUrl } from "../lib/session-access";
import { formatUptime, shortId } from "../lib/session-identity";

// This page is the heaviest thing the console does: the list fans out to every
// worker, and each visible row additionally pulls a CDP screenshot off the
// browser it is showing. Both costs land on the fleet, not on this box, so both
// are held to the same rule: only poll for something a human is currently
// looking at.
const SESSIONS_REFETCH_MS = 5_000;
const THUMBNAIL_REFRESH_MS = 15_000;

interface LiveSession {
	launchId: string;
	referenceId: string;
	/** The worker's REST session id for the same browser, when it reports one. */
	sessionId?: string | null;
	browserGuid: string;
	launchedAt: string;
	uptime: number;
	readyState: number;
	connectionCount: number;
	hostname: string;
}

interface SessionsPayload {
	sessions: LiveSession[];
	errors: { hostname: string; message: string }[];
}

type SortKey = "uptime" | "worker";

/**
 * True while the element is on screen and the tab is in the foreground.
 *
 * A screenshot costs a CDP round trip on the worker holding the browser, so a
 * row scrolled past the fold or sitting in a background tab must not pay for
 * one. On a fleet with a hundred live sessions the difference is a handful of
 * captures per refresh instead of a hundred.
 */
function useOnScreen(ref: React.RefObject<Element | null>): boolean {
	const [onScreen, setOnScreen] = useState(false);

	useEffect(() => {
		const node = ref.current;
		if (!node) return;

		let intersecting = false;
		let visible = document.visibilityState === "visible";
		const sync = () => setOnScreen(intersecting && visible);

		const observer = new IntersectionObserver(
			(entries) => {
				intersecting = entries.some((e) => e.isIntersecting);
				sync();
			},
			// Start a little before the row arrives so scrolling does not reveal a
			// blank tile, but not so early that the whole list counts as visible.
			{ rootMargin: "200px 0px" },
		);
		observer.observe(node);

		const onVisibility = () => {
			visible = document.visibilityState === "visible";
			sync();
		};
		document.addEventListener("visibilitychange", onVisibility);

		return () => {
			observer.disconnect();
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [ref]);

	return onScreen;
}

/**
 * A live still of the page, refreshed on a slow cadence while it is on screen.
 *
 * The refresh is a plain interval rather than a query: there is nothing to
 * cache or share, only a cache-busting stamp, and modelling a timer as server
 * state meant the app-wide invalidation on operation completion could make
 * every thumbnail in the fleet recapture at the same instant.
 *
 * The image comes from cluster-manager rather than the worker: the worker's
 * `/browsers/*` routes now want a per-launch capability an `<img>` has no way
 * to carry. See lib/session-access.
 */
const SessionThumbnail = memo(function SessionThumbnail({
	session,
}: {
	session: LiveSession;
}) {
	const [failed, setFailed] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const onScreen = useOnScreen(containerRef);
	const [stamp, setStamp] = useState(() => Date.now());

	useEffect(() => {
		if (!onScreen) return;
		// Coming back into view after the row was scrolled past, or after the tab
		// was in the background, would otherwise show a still from minutes ago
		// until the next tick. Refresh straight away when the current one is
		// already stale, then settle into the interval.
		setStamp((prev) =>
			Date.now() - prev >= THUMBNAIL_REFRESH_MS ? Date.now() : prev,
		);
		const id = setInterval(() => setStamp(Date.now()), THUMBNAIL_REFRESH_MS);
		return () => clearInterval(id);
	}, [onScreen]);

	const src = sessionThumbnailUrl(session.hostname, session.launchId, stamp);

	return (
		<div
			ref={containerRef}
			className="h-14 w-24 shrink-0 overflow-hidden rounded-md bg-muted ring-1 ring-foreground/10"
		>
			{failed ? (
				<span className="grid size-full place-items-center text-[0.65rem] text-muted-foreground">
					no preview
				</span>
			) : onScreen ? (
				<img
					src={src}
					alt=""
					width={96}
					height={56}
					onError={() => setFailed(true)}
					className="size-full object-cover object-top"
				/>
			) : null}
		</div>
	);
});

/**
 * Memoised: the list refetches every 5s and hands back a fresh array each time,
 * so without this every row and every thumbnail re-renders on a poll that
 * usually changed nothing.
 */
const SessionRow = memo(function SessionRow({
	session,
	onKill,
	killing,
}: {
	session: LiveSession;
	onKill: (s: LiveSession) => void;
	/** True only for the row being killed, not for every row on the page. */
	killing: boolean;
}) {
	const viewer = `/sessions/${session.hostname}/${session.launchId}`;
	const attached = session.connectionCount > 0;
	return (
		// content-visibility lets the browser skip layout and paint for rows below
		// the fold; the reserved height keeps the scrollbar honest.
		<div className="flex items-center gap-4 border-b border-border/60 py-3 [content-visibility:auto] [contain-intrinsic-size:auto_5rem]">
			<Link href={viewer} aria-label={`Open session ${session.launchId}`}>
				<SessionThumbnail session={session} />
			</Link>

			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<Link
						href={viewer}
						className="truncate font-mono text-[0.8125rem] hover:underline"
					>
						{session.referenceId || session.launchId}
					</Link>
					{/* Variant plus text, never colour alone. */}
					<Badge variant={attached ? "success" : "outline"}>
						{attached ? `${session.connectionCount} attached` : "idle"}
					</Badge>
				</div>
				<div className="mt-0.5 truncate text-xs text-muted-foreground">
					<span className="font-mono">{session.hostname}</span>
					<span aria-hidden> · </span>
					<span className="font-mono">{formatUptime(session.uptime)}</span>
					{session.referenceId ? (
						<>
							<span aria-hidden> · </span>
							<span className="font-mono">{shortId(session.launchId)}</span>
						</>
					) : null}
				</div>
			</div>

			<div className="flex shrink-0 items-center gap-1">
				<Button asChild size="sm" variant="ghost">
					<Link href={`${viewer}?view=vnc`}>
						<MonitorPlay aria-hidden />
						Watch
					</Link>
				</Button>
				<Button asChild size="sm" variant="ghost">
					<Link href={`${viewer}?view=devtools`}>
						<SquareTerminal aria-hidden />
						Inspect
					</Link>
				</Button>
				<Button
					size="sm"
					variant="ghost"
					disabled={killing}
					onClick={() => onKill(session)}
					aria-label={`Stop session ${session.referenceId || shortId(session.launchId)} on ${session.hostname}`}
				>
					<Trash2 aria-hidden />
				</Button>
			</div>
		</div>
	);
});

function LoadingRows() {
	return (
		<div>
			{[0, 1, 2].map((i) => (
				<div
					key={i}
					className="flex items-center gap-4 border-b border-border/60 py-3"
				>
					<Skeleton className="h-14 w-24 rounded-md" />
					<div className="flex-1 space-y-2">
						<Skeleton className="h-4 w-56" />
						<Skeleton className="h-3 w-40" />
					</div>
				</div>
			))}
		</div>
	);
}

/** Teaches both ways to create a session rather than just reporting zero. */
function EmptyState() {
	return (
		<div className="py-16 text-center">
			<p className="text-sm font-medium">No live browsers</p>
			<p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
				Launch one from a worker on the{" "}
				<Link href="/workers" className="underline underline-offset-2">
					Workers
				</Link>{" "}
				page, or connect an automation client to{" "}
				<code className="rounded-sm bg-muted px-1 py-0.5 font-mono text-xs">
					wss://{window.location.host}/launch
				</code>
				.
			</p>
		</div>
	);
}

export function Sessions() {
	const queryClient = useQueryClient();
	const [sort, setSort] = useState<SortKey>("uptime");

	const query = useQuery({
		queryKey: ["fleet-sessions"],
		queryFn: async () => {
			const res = (await unwrap(api.api.workers.sessions.get())) as {
				data: SessionsPayload;
			};
			return res.data;
		},
		refetchInterval: SESSIONS_REFETCH_MS,
		// One request here becomes one request to every worker in the fleet.
		// A backgrounded tab left open overnight has no reason to keep paying it.
		refetchIntervalInBackground: false,
	});

	// Which session is being killed, so one pending request does not disable the
	// kill button on every other row.
	const [killingId, setKillingId] = useState<string | null>(null);
	// Killing a browser loses whatever it was doing, and this list is the one
	// place an operator sees every session in the fleet at once — the row under
	// the pointer is not necessarily the row they meant. Ask first.
	const [confirmKill, setConfirmKill] = useState<StopSessionTarget | null>(
		null,
	);

	const kill = useMutation({
		mutationFn: async (target: StopSessionTarget) =>
			unwrap(
				api.api
					.workers({ hostname: target.hostname })
					.sessions.stop.post({ sessionId: target.id }),
			),
		onSuccess: () => {
			toast.success("Session stopped");
			void queryClient.invalidateQueries({ queryKey: ["fleet-sessions"] });
		},
		onError: (err: Error) => toast.error(err.message),
		onSettled: () => {
			setKillingId(null);
			setConfirmKill(null);
		},
	});

	// Stable across renders so the memoised rows are not invalidated by a new
	// handler identity on every poll.
	const handleKill = useCallback((session: LiveSession) => {
		setConfirmKill({
			kind: "launch",
			hostname: session.hostname,
			id: session.launchId,
			// Carried so the dialog can still name the browser if it exited
			// between this list's last poll and the operator's click.
			sessionId: session.sessionId ?? null,
			referenceId: session.referenceId || null,
			uptime: session.uptime,
			connectionCount: session.connectionCount,
		});
	}, []);

	const killMutate = kill.mutate;
	const confirmKillNow = useCallback(
		(target: StopSessionTarget) => {
			setKillingId(target.id);
			killMutate(target);
		},
		[killMutate],
	);

	const sessions = useMemo(() => {
		// Copy before sorting: the array belongs to the query cache.
		// (`toSorted` would say this better but is above this project's lib target.)
		const list = [...(query.data?.sessions ?? [])];
		list.sort((a, b) =>
			sort === "uptime"
				? b.uptime - a.uptime
				: a.hostname.localeCompare(b.hostname) || b.uptime - a.uptime,
		);
		return list;
	}, [query.data?.sessions, sort]);

	const errors = query.data?.errors ?? [];

	return (
		<div className="space-y-6 p-6">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">Live sessions</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						Every browser running across the fleet. Watch one over VNC or attach
						DevTools.
					</p>
				</div>
				<fieldset className="flex items-center gap-1 border-0 p-0">
					<legend className="sr-only">Sort sessions</legend>
					<Button
						size="sm"
						variant={sort === "uptime" ? "outline" : "ghost"}
						aria-pressed={sort === "uptime"}
						onClick={() => setSort("uptime")}
					>
						Longest running
					</Button>
					<Button
						size="sm"
						variant={sort === "worker" ? "outline" : "ghost"}
						aria-pressed={sort === "worker"}
						onClick={() => setSort("worker")}
					>
						By worker
					</Button>
				</fieldset>
			</div>

			{/* Naming the unreachable workers matters: silently dropping them
			    would under-report the fleet and hide the actual problem. */}
			{errors.length > 0 ? (
				<div className="rounded-xl border border-border/60 p-4">
					<div className="flex items-center gap-2 text-sm font-medium">
						<CircleAlert
							className="size-4 text-[var(--status-warning)]"
							aria-hidden
						/>
						{errors.length} worker{errors.length > 1 ? "s" : ""} could not be
						reached
					</div>
					<ul className="mt-2 space-y-1">
						{errors.map((e) => (
							<li key={e.hostname} className="text-xs text-muted-foreground">
								<span className="font-mono">{e.hostname}</span> — {e.message}
							</li>
						))}
					</ul>
				</div>
			) : null}

			<div>
				{query.isLoading ? (
					<LoadingRows />
				) : sessions.length === 0 ? (
					<EmptyState />
				) : (
					<div className="border-t border-border/60">
						{sessions.map((s) => (
							<SessionRow
								key={`${s.hostname}:${s.launchId}`}
								session={s}
								killing={killingId === s.launchId}
								onKill={handleKill}
							/>
						))}
					</div>
				)}
			</div>

			<StopSessionDialog
				target={confirmKill}
				pending={kill.isPending}
				onOpenChange={() => setConfirmKill(null)}
				onConfirm={confirmKillNow}
			/>
		</div>
	);
}
